<script setup lang="ts">
import router from '@/router';
import { reactive, ref, computed, markRaw } from "vue";
import { useI18n } from "vue-i18n";
import { VDateInput } from "vuetify/labs/components";
import { useRoute } from "vue-router";
import { useDisplay } from "vuetify";
import { QuillEditor } from '@vueup/vue-quill'
import '@vueup/vue-quill/dist/vue-quill.snow.css'

import MainTitleComponent from "@/components/ui/MainTitleComponent.vue";
import DividerComponent from "@/components/ui/DividerComponent.vue";
import AssetComponent from '@/components/mandate/AssetComponent.vue';
import DocumentComponent from '@/components/mandate/DocumentComponent.vue';
import EntryFeeTiersComponent from '@/components/mandate/EntryFeeTiersComponent.vue';
import MandateService from "@/services/mandate/mandateService.ts";
import { useAuthStore } from "@/stores/authStore.ts";
import { useSnackbarStore } from "@/stores/snackbarStore.ts";
import validators from "@/utils/validators.ts";
import { areEntryFeeTiersValid, toEntryFeeTiersPayload } from '@/utils/entryFeeTiers.ts';
import { displayToast } from '@/utils/toastUtils.ts';
import { displayModal } from '@/utils/modalUtils.ts';
import {
  formatDate,
  formatPercentage
} from '@/utils/formatters.ts';
import {
  MandateDocumentCategoryEnum,
  MandateBoStatusEnum
} from "@/enums/mandate_enum.ts";

import type {
  UpdateMandateRequest,
  AssetRow,
  MandateCurrency,
  MandateBoStatusKey,
  MandateDocument,
  MandateResponse,
  MandateDocumentWithDic,
  MandateInvestmentLimit
} from '@/types/services/mandate/mandate.type.ts';
import type { ValidationRule } from "@/types/utils/validators.type.ts";
import type { MeResponse } from '@/types/services/auth/auth.type.ts';
import type { DataTableHeader } from 'vuetify/framework';
import type { AploAsset } from '@/types/services/aplo/aplo.type.ts';

const { t } = useI18n()
const snackbarStore = useSnackbarStore()
const authStore = useAuthStore()
const route = useRoute()
const { smAndDown } = useDisplay()

const step = ref<number>(1)
const loading = ref<boolean>(false)

// -- Mandate data from route meta --//
const assetNames = ref<AploAsset[]>(route.meta.assets as AploAsset[] || [])
const investmentLimit = ref<MandateInvestmentLimit>(route.meta.investmentLimit as MandateInvestmentLimit
  || { minInvestment: 0, maxInvestment: 0 }
);
//--- Update mandate form ---//
const nameRules = ref<ValidationRule[]>([...validators.mandateNameRules])
const minimumInvestmentRules = computed<ValidationRule[]>(() =>
  validators.mandateMinimumInvestmentRules(investmentLimit.value.minInvestment)
)
const maximumInvestmentRules = computed<ValidationRule[]>(() =>
  validators.mandateMaximumInvestmentRules(
    investmentLimit.value.minInvestment + 1,
    investmentLimit.value.maxInvestment
  )
)
const recommendedDurationRules = ref<ValidationRule[]>([...validators.mandateRecommendedDurationRules])
const availableAtRules = ref<ValidationRule[]>([...validators.mandateAvailableAtRules])
const youtubeRules = ref<ValidationRule[]>([...validators.mandateYoutubeRules])
const percentageRules = ref<ValidationRule[]>([...validators.mandatePercentageRules])

const currencies = ref<MandateCurrency[]>(["EUR"])

const form = reactive<MandateResponse>({
  ...route.meta.mandate as MandateResponse,
  minimumInvestment: (route.meta.mandate as MandateResponse)?.minimumInvestment / 100,
  maximumInvestment: (route.meta.mandate as MandateResponse)?.maximumInvestment / 100,
  fees: {
    subscriptionFee: Number((route.meta.mandate as MandateResponse)?.fees.subscriptionFee ?? 0),
    managementFee: Number((route.meta.mandate as MandateResponse)?.fees.managementFee ?? 0),
    managementPeriodicity: (route.meta.mandate as MandateResponse)?.fees.managementPeriodicity ?? null,
    exitFee: Number((route.meta.mandate as MandateResponse)?.fees.exitFee ?? 0),
    earlyExitPenalty: Number((route.meta.mandate as MandateResponse)?.fees.earlyExitPenalty ?? 0),
    earlyExitMinDuration: Number((route.meta.mandate as MandateResponse)?.fees.earlyExitMinDuration ?? 0),
    performanceFee: Number((route.meta.mandate as MandateResponse)?.fees.performanceFee ?? 0),
    entryFeeTiers: ((route.meta.mandate as MandateResponse)?.fees.entryFeeTiers ?? []).map(tier => ({ ...tier }))
  }
})
const isDraft = ref<boolean>(form.boStatus === MandateBoStatusEnum.DRAFT)
const isPending = ref<boolean>(form.boStatus === MandateBoStatusEnum.PENDING)
const isPendingDic = ref<boolean>(form.boStatus === MandateBoStatusEnum.PENDING_DIC)
const isRefused = ref<boolean>(form.boStatus === MandateBoStatusEnum.REFUSED)
const isValidated = ref<boolean>(form.boStatus === MandateBoStatusEnum.VALIDATED)

// True only for the DAM who created this mandate (never for an admin, who has no "own" mandate).
const isOwnMandate = computed<boolean>(() =>
  form.authorId === Number((authStore.user as MeResponse).id)
)

// Admins can edit a mandate's core fields regardless of its current bo status (draft, pending,
// pending_dic, refused) via `PUT /mandate/{id}`. A DAM can do the same, but only for pending/
// pending_dic on a mandate they created themselves — otherwise (another DAM's mandate) they're
// restricted to draft/refused like before. Excluded even for admins: a validated mandate (this
// endpoint only accepts draft/pending/pending_dic as a target status, so saving would force it
// out of validated) and the platform's custody mandate (the backend refuses to update it at all).
const canEditFields = computed<boolean>(() =>
  isDraft.value ||
  isRefused.value ||
  (!isValidated.value && !form.custody && (
    authStore.isAdmin || (isOwnMandate.value && (isPending.value || isPendingDic.value))
  ))
)

const initialDocumentsList = ref<MandateDocument[]>([...form.documents])
const dicUserNotification = ref<boolean>(false)
const isFormLocked = ref<boolean>(
  form.boStatus === MandateBoStatusEnum.PENDING
  || form.boStatus === MandateBoStatusEnum.PENDING_DIC
)

const availableAssetNames = (index: number | null = null) => {
  const selected = form.assets.map((asset, assetIndex) =>
    assetIndex !== index ? asset.name : null
  ).filter(Boolean)
  return assetNames.value.filter(asset => !selected.includes(asset.name))
}
const canAddAsset = computed(() => form.assets.length < assetNames.value.length)
const totalPercentage = computed<number>(() =>
  form.assets.reduce((sum, asset) => sum + (Number(asset.percentage) || 0), 0)
)

const assetHeaders = computed<DataTableHeader[]>(() => [
  {
    title: t("views.updateMandate.table.asset.name"),
    key: "name",
    align: "start",
    sortable: false
  },
  {
    title: t("views.updateMandate.table.asset.percentage"),
    key: "percentage",
    align: "start",
    sortable: false
  },
  {
    title: t('views.updateMandate.table.asset.action'),
    key: 'actions',
    sortable: false,
    width: '100px',
    align: 'end'
  }
])
async function addAsset(): Promise<void> {
  const modal = await displayModal({
    component: markRaw(AssetComponent),
    props: {
      availableAssets: availableAssetNames(),
      initial: null
    },
  })

  if (modal && typeof modal === 'object') {
    const { id, name, percentage } = modal as AssetRow;
    form.assets.push({ id, name, percentage })
  }
}
async function editAsset(index: number): Promise<void> {
  if (!canEditFields.value) {
    return
  }

  const current = form.assets[index]

  const modal = await displayModal({
    component: markRaw(AssetComponent),
    props: {
      availableAssets: availableAssetNames(index),
      initial: { ...current }
    },
  })

  if (modal && typeof modal === 'object') {
    const { id, name, percentage } = modal as AssetRow;
    form.assets[index] = { id, name, percentage }
  }
}
function removeAsset(index: number): void {
  form.assets.splice(index, 1)
}

const documentHeaders = computed<DataTableHeader[]>(() => [
  {
    title: t("views.updateMandate.table.document.name"),
    key: "name",
    align: "start",
    sortable: false
  },
  {
    title: t("views.updateMandate.table.document.category"),
    key: "category",
    align: "start",
    sortable: false
  },
  {
    title: t("views.updateMandate.table.document.createdAt"),
    key: "createdAt",
    align: "start",
    sortable: false
  },
  {
    title: t('views.updateMandate.table.document.action'),
    key: 'actions',
    sortable: false,
    width: '100px',
    align: 'end'
  }
])

function addDocumentDicAvailable(): boolean {
  return form.documents.filter(doc => doc.category === MandateDocumentCategoryEnum.DIC).length <
    initialDocumentsList.value.filter(doc => doc.category === MandateDocumentCategoryEnum.DIC).length + 1;
}

async function addDocument(): Promise<void> {
  const modal = await displayModal({
    component: markRaw(DocumentComponent),
    props: {
      isDraft: isDraft.value,
      isPending: isPending.value,
      isRefused: isRefused.value,
    }
  })

  if (modal && typeof modal === 'object') {
    const { id, name, category, file, isUserNotified } = modal as MandateDocumentWithDic;
    const document = { id, name, category, created_at: new Date().toISOString(), file }

    if (!addDocumentDicAvailable() && category === MandateDocumentCategoryEnum.DIC) {
      displayToast(t('views.updateMandate.newDicAlreadyAdded'), 'error');
      return;
    }

    dicUserNotification.value = isUserNotified || false;

    form.documents.unshift(document)
  }
}
function removeDocument(document: MandateDocument) {
  form.documents.splice(form.documents.indexOf(document), 1)
}

async function downloadDocument(mandateDocument: MandateDocument) {
  if (!mandateDocument.id) {
    return
  }

  const url = await MandateService.downloadDocument(form.id, mandateDocument.id)
  window.open(url, '_blank')
}

function categoryLabel(category: string) {
  return t(`views.updateMandate.table.document.label.${category}`)
}

const canSubmitToReview = computed<boolean>(() => {
  if (canEditFields.value) {
    return Boolean(
      !!form.name &&
      !!form.description &&
      form.assets.length > 0 &&
      form.risk != null &&
      !!form.currency &&
      form.minimumInvestment != null &&
      form.maximumInvestment != null &&
      form.recommendedDuration != null &&
      !!form.availableAt &&
      !!form.strategyOperation &&
      !!form.presentation &&
      form.documents.length > 0 &&
      form.documents.some(doc => doc.category === MandateDocumentCategoryEnum.DIC) &&
      totalPercentage.value === 100 &&
      form.fees.subscriptionFee != null &&
      form.fees.managementFee != null &&
      form.fees.exitFee != null &&
      form.fees.earlyExitPenalty != null &&
      form.fees.earlyExitMinDuration != null &&
      form.fees.performanceFee != null &&
      areEntryFeeTiersValid(form.fees.entryFeeTiers)
    )
  } else {
    return JSON.stringify(form.documents) !== JSON.stringify(initialDocumentsList.value);
  }
})

// The main action button reuses the same "PUT /mandate" call whether the mandate is being
// submitted for the first time (draft/refused) or already awaiting/holding a status
// (pending/pending_dic edited by an admin or by its own DAM) — in the latter case it doesn't
// actually change the bo status, so labeling it "Soumettre à validation" would be misleading.
const isAlreadySubmitted = computed<boolean>(() => isPending.value || isPendingDic.value)

const submitButtonLabel = computed<string>(() =>
  isAlreadySubmitted.value ? t('views.updateMandate.buttons.save') : t('views.updateMandate.buttons.create')
)

const submitButtonIcon = computed<string>(() => isAlreadySubmitted.value ? 'PhFloppyDisk' : 'PhCheck')

async function updateMandate(status: MandateBoStatusKey): Promise<void> {
  loading.value = true

  if (dicUserNotification.value) {
    status = MandateBoStatusEnum.PENDING_DIC
  }

  const mandate: UpdateMandateRequest = {
    id: form.id,
    name: form.name,
    description: form.description,
    assets: form.assets,
    risk: form.risk,
    currency: form.currency,
    minimumInvestment: form.minimumInvestment,
    maximumInvestment: form.maximumInvestment,
    recommendedDuration: form.recommendedDuration,
    strategyOperation: form.strategyOperation,
    presentation: form.presentation,
    availableAt: form.availableAt ? new Date(form.availableAt).toISOString() : "",
    closedAt: form.closedAt ? new Date(form.closedAt).toISOString() : "",
    youtubeUrl: form.youtubeUrl,
    boStatus: status,
    fees: {
      subscriptionFee: Number(form.fees.subscriptionFee),
      managementFee: Number(form.fees.managementFee),
      managementPeriodicity: form.fees.managementPeriodicity,
      exitFee: Number(form.fees.exitFee),
      earlyExitPenalty: Number(form.fees.earlyExitPenalty),
      earlyExitMinDuration: Number(form.fees.earlyExitMinDuration),
      performanceFee: Number(form.fees.performanceFee),
      // Always the full list: omitted would keep the existing tiers, [] removes them all.
      entryFeeTiers: toEntryFeeTiersPayload(form.fees.entryFeeTiers)
    }
  }

  if (!form.name) {
    snackbarStore.openSnackbar({
      text: t("views.updateMandate.snackbar.nameRequired"),
      color: "error"
    })
    loading.value = false
    return
  }

  try {
    const response: UpdateMandateRequest = await MandateService.updateMandate(mandate, dicUserNotification.value)

    // Handle document upload/deletion
    if (form.documents.length > initialDocumentsList.value.length && mandate.id) {
      const newDocuments: MandateDocument[] = form.documents.filter(
        document => !initialDocumentsList.value.some(initDoc => initDoc.id === document.id)
      );
      await MandateService.uploadDocument(mandate.id, newDocuments);
    } else if (form.documents.length < initialDocumentsList.value.length && mandate.id) {
      const removedDocuments: MandateDocument[] = initialDocumentsList.value.filter(
        initDocument => !form.documents.some(document => document.id === initDocument.id)
      );
      for (const document of removedDocuments) {
        if (document?.id) {
          await MandateService.deleteDocument(mandate.id, document.id);
        }
      }
    }

    const updatedDocuments = form.documents;

    if (status === MandateBoStatusEnum.PENDING || status === MandateBoStatusEnum.PENDING_DIC) {
      await router.push('/mandates')
    } else {
      Object.assign(form, {
        ...response,
        documents: updatedDocuments,
        minimumInvestment: response.minimumInvestment / 100,
        maximumInvestment: response.maximumInvestment / 100,
        availableAt: response.availableAt ? new Date(response.availableAt) : null,
        closedAt: response.closedAt ? new Date(response.closedAt) : null,
        fees: {
          subscriptionFee: Number(response.fees.subscriptionFee ?? 0),
          managementFee: Number(response.fees.managementFee ?? 0),
          exitFee: Number(response.fees.exitFee ?? 0),
          earlyExitPenalty: Number(response.fees.earlyExitPenalty ?? 0),
          earlyExitMinDuration: response.fees.earlyExitMinDuration,
          performanceFee: Number(response.fees.performanceFee ?? 0),
          entryFeeTiers: response.fees.entryFeeTiers ?? [],
        }
      })
    }

    snackbarStore.openSnackbar({
      text: t("views.updateMandate.snackbar.update"),
      color: "success"
    })
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <!-- Main title -->
  <MainTitleComponent>
    {{ t("views.updateMandate.title") }}

    <template #actions>
      <v-col cols="12" sm="auto" class="d-flex flex-column flex-sm-row">
        <v-btn
          v-if="isDraft"
          :disabled="loading"
          :loading="loading"
          class="mr-0 mr-sm-4 mb-4 mb-sm-0"
          type="submit"
          variant="outlined"
          @click="updateMandate('draft')"
        >
          {{ t("views.updateMandate.buttons.draft") }}
          <template #prepend>
            <v-icon icon="PhPencilSimple" />
          </template>
        </v-btn>

        <v-btn
          :disabled="!canSubmitToReview || loading"
          :loading="loading"
          type="submit"
          @click="updateMandate(isDraft || isPending || isRefused ? 'pending' : 'pending_dic')"
        >
          {{ submitButtonLabel }}
          <template #prepend>
            <v-icon :icon="submitButtonIcon" />
          </template>
        </v-btn>
      </v-col>
    </template>
  </MainTitleComponent>

  <v-stepper v-model="step" :elevation="0" :mobile="smAndDown">
    <v-stepper-header>
      <v-divider />

      <v-stepper-item
        :title="t('views.updateMandate.stepper.step1')"
        :value="1"
        color="primary"
        editable
      />

      <v-divider />

      <v-stepper-item
        :title="t('views.updateMandate.stepper.step2')"
        :value="2"
        color="primary"
        editable
      />

      <v-divider />

      <v-stepper-item
        :title="t('views.updateMandate.stepper.step3')"
        :value="3"
        color="primary"
        editable
      />

      <v-divider />

      <v-stepper-item
        :title="t('views.updateMandate.stepper.step4')"
        :value="4"
        color="primary"
        editable
      />

      <v-divider />
    </v-stepper-header>

    <v-stepper-window>
      <!-- STEP 1 -->
      <v-stepper-window-item :value="1">
        <v-row>
          <v-col cols="12" class="d-flex align-center">
            <v-icon class="mr-2" color="primary" icon="PhInfo"/>
            <h2 class="subtitle">{{ t("views.updateMandate.subtitle1") }}</h2>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" sm="6" md="3">
            <v-text-field
              v-model="form.name"
              :disabled="!canEditFields"
              :label="t('views.updateMandate.inputs.name.label')"
              :placeholder="t('views.updateMandate.inputs.name.placeholder')"
              :rules="nameRules"
              hide-details="auto"
            />
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-select
              v-model="form.currency"
              :disabled="!canEditFields"
              :items="currencies"
              :label="t('views.updateMandate.inputs.currency.label')"
              hide-details="auto"
            />
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-date-input
              v-model="form.availableAt"
              :disabled="!canEditFields"
              :label="t('views.updateMandate.inputs.availableAt.label')"
              :placeholder="t('views.updateMandate.inputs.availableAt.placeholder')"
              :rules="availableAtRules"
              hide-details="auto"
              persistent-placeholder
              prepend-icon=""
            />
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-date-input
              v-model="form.closedAt"
              :label="t('views.updateMandate.inputs.closedAt.label')"
              :placeholder="t('views.updateMandate.inputs.closedAt.placeholder')"
              disabled
              hide-details="auto"
              persistent-placeholder
              prepend-icon=""
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" sm="6" md="3">
            <v-select
              v-model="form.risk"
              :disabled="!canEditFields"
              :items="[1, 2, 3, 4, 5, 6, 7]"
              :label="t('views.updateMandate.inputs.risk.label')"
              color="primary"
              hide-details="auto"
            />
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-text-field
              v-model.number="form.recommendedDuration"
              :disabled="!canEditFields"
              :label="t('views.updateMandate.inputs.recommendedDuration.label')"
              :min="1"
              :rules="recommendedDurationRules"
              :suffix="t('views.updateMandate.inputs.recommendedDuration.suffix', form.recommendedDuration)"
              density="compact"
              hide-details="auto"
              type="number"
            />
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-text-field
              v-model.number="form.minimumInvestment"
              :disabled="!canEditFields"
              :label="t('views.updateMandate.inputs.minimumInvestment.label')"
              :min="0"
              :rules="minimumInvestmentRules"
              density="compact"
              hide-details="auto"
              type="number"
            >
              <template #append-inner>
                <v-icon icon="PhCurrencyEur"/>
              </template>
            </v-text-field>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-text-field
              v-model.number="form.maximumInvestment"
              :disabled="!canEditFields"
              :label="t('views.updateMandate.inputs.maximumInvestment.label')"
              :min="1"
              :rules="maximumInvestmentRules"
              density="compact"
              hide-details="auto"
              type="number"
            >
              <template #append-inner>
                <v-icon icon="PhCurrencyEur"/>
              </template>
            </v-text-field>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" md="6" :class="{ 'quill-is-disabled': !canEditFields }">
            <QuillEditor
              v-model:content="form.description"
              :read-only="!canEditFields"
              :placeholder="(!canEditFields) ? '' : t('views.updateMandate.inputs.description.placeholder')"
              :toolbar="[
                ['bold', 'italic', 'underline', 'strike'],
                [{ list: 'ordered' }, { list: 'bullet' }],
                [{ header: [1, 2, 3, false] }],
                ['clean']
              ]"
              content-type="html"
            />
          </v-col>
          <v-col cols="12" md="6" :class="{ 'quill-is-disabled': !canEditFields }">
            <QuillEditor
              v-model:content="form.strategyOperation"
              :read-only="!canEditFields"
              :placeholder="(!canEditFields) ? '' : t('views.updateMandate.inputs.strategyOperation.placeholder')"
              :toolbar="[
                ['bold', 'italic', 'underline', 'strike'],
                [{ list: 'ordered' }, { list: 'bullet' }],
                [{ header: [1, 2, 3, false] }],
                ['clean']
              ]"
              class="quill-editor"
              content-type="html"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" md="6" :class="{ 'quill-is-disabled': !canEditFields }">
            <QuillEditor
              v-model:content="form.presentation"
              :read-only="!canEditFields"
              :placeholder="(!canEditFields) ? '' : t('views.updateMandate.inputs.presentation.placeholder')"
              :toolbar="[
                ['bold', 'italic', 'underline', 'strike'],
                [{ list: 'ordered' }, { list: 'bullet' }],
                [{ header: [1, 2, 3, false] }],
                ['clean']
              ]"
              content-type="html"
            />
          </v-col>
        </v-row>

        <DividerComponent />

        <v-row>
          <v-col cols="12">
            <div class="d-flex align-center">
              <v-icon class="mr-2" color="primary" icon="PhYoutubeLogo"/>
              <h2 class="subtitle">{{ t("views.updateMandate.subtitle2") }}</h2>
            </div>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="form.youtubeUrl"
              :disabled="!canEditFields"
              :label="t('views.updateMandate.inputs.youtube.label')"
              :placeholder="t('views.updateMandate.inputs.youtube.placeholder')"
              :rules="youtubeRules"
              hide-details="auto"
            />
          </v-col>
        </v-row>
      </v-stepper-window-item>

      <!-- STEP 2 -->
      <v-stepper-window-item :value="2">
        <v-row align="center">
          <v-col cols="12" class="d-flex flex-column flex-sm-row justify-space-between">
            <div class="d-flex align-center">
              <v-icon color="primary" class="mr-2" icon="PhMoney"/>
              <h2 class="subtitle mr-2">
                {{ t("views.updateMandate.subtitle3") }}
                <v-chip
                  :color="totalPercentage === 100 ? 'success' : (totalPercentage > 100 ? 'error' : 'warning')"
                  size="small"
                >
                  {{ totalPercentage }}%
                </v-chip>
              </h2>
            </div>

            <v-btn
              :disabled="!canAddAsset || (!canEditFields)"
              class="mt-4 mt-sm-0"
              type="submit"
              @click="canAddAsset && addAsset()">
              {{ t("views.updateMandate.addAsset") }}
              <template #prepend>
                <v-icon icon="PhPlus"/>
              </template>
            </v-btn>
          </v-col>
        </v-row>

        <v-row>
          <v-col>
            <v-data-table
              :headers="assetHeaders"
              :items="form.assets"
              :items-per-page="-1"
              :no-data-text="t('common.noData')"
              class="rounded-std"
              hover
            >
              <template #item="{ item, index}">
                <tr @click="editAsset(index)" style="cursor: pointer">
                  <td>{{ item.name }}</td>
                  <td>{{ formatPercentage(item.percentage) }}</td>
                  <td>
                    <v-btn
                      :disabled="form.assets.length === 1 || (!canEditFields)"
                      variant="plain"
                      @click.stop.prevent="removeAsset(index)"
                    >
                      <v-icon icon="PhTrash"/>
                    </v-btn>
                  </td>
                </tr>
              </template>
              <template #bottom />
            </v-data-table>
          </v-col>
        </v-row>
      </v-stepper-window-item>

      <!-- STEP 3 -->
      <v-stepper-window-item :value="3">
        <v-row>
          <v-col cols="12" class="d-flex flex-column flex-sm-row justify-space-between">
            <div class="d-flex align-center">
              <v-icon class="mr-2" color="primary" icon="PhFileText"/>
              <h2 class="subtitle">{{ t("views.updateMandate.subtitle4") }}</h2>
            </div>

            <v-btn
              :disabled="isFormLocked"
              class="mt-4 mt-sm-0"
              type="submit"
              @click="addDocument"
            >
              <v-icon icon="PhPlus" class="mr-1" />
              {{ t('views.updateMandate.buttons.addDocument') }}
            </v-btn>
          </v-col>
        </v-row>

        <v-row>
          <v-col>
            <v-data-table
              :headers="documentHeaders"
              :items="form.documents"
              :items-per-page="-1"
              :no-data-text="t('common.noData')"
              class="rounded-std"
              hover
            >
              <template #item="{ item }">
                <tr>
                  <td>{{ item.name }}</td>
                  <td>{{ categoryLabel(item.category) }}</td>
                  <td>{{ formatDate(item.created_at) }}</td>
                  <td>
                    <v-btn
                      :disabled="!item.id"
                      class="pa-0 ma-2 td-btn"
                      variant="plain"
                      @click.stop.prevent="downloadDocument(item)"
                    >
                      <v-icon icon="PhDownload" />
                    </v-btn>
                    <v-btn
                      :disabled="isFormLocked"
                      variant="plain"
                      class="pa-0 ma-2 td-btn"
                      @click.stop.prevent="removeDocument(item)"
                    >
                      <v-icon icon="PhTrash"/>
                    </v-btn>
                  </td>
                </tr>
              </template>
              <template #bottom />
            </v-data-table>
          </v-col>
        </v-row>
      </v-stepper-window-item>

      <!-- STEP 4 -->
      <v-stepper-window-item :value="4">
        <v-row>
          <v-col class="d-flex align-center" cols="12">
            <v-icon class="mr-2" color="primary" icon="PhSignIn"/>
            <h2 class="subtitle">{{ t("views.updateMandate.subtitle5") }}</h2>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" md="3" sm="6">
            <v-text-field
              v-model.number="form.fees.subscriptionFee"
              :disabled="!canEditFields"
              :label="t('views.updateMandate.inputs.subscriptionFee.label')"
              :min="0"
              :rules="percentageRules"
              density="compact"
              hide-details="auto"
              suffix="%"
              type="number"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" md="9">
            <EntryFeeTiersComponent v-model="form.fees.entryFeeTiers" :disabled="!canEditFields" />
          </v-col>
        </v-row>

        <DividerComponent />

        <v-row>
          <v-col class="d-flex align-center" cols="12">
            <v-icon class="mr-2" color="primary" icon="PhReceipt"/>
            <h2 class="subtitle">{{ t("views.updateMandate.subtitle6") }}</h2>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" md="3" sm="6">
            <v-text-field
              v-model.number="form.fees.managementFee"
              :disabled="!canEditFields"
              :label="t('views.updateMandate.inputs.managementFee.label')"
              :min="0"
              :rules="percentageRules"
              density="compact"
              hide-details="auto"
              suffix="%"
              type="number"
            />
          </v-col>
          <v-col cols="12" md="3" sm="6">
            <v-text-field
              v-model.number="form.fees.performanceFee"
              :disabled="!canEditFields"
              :label="t('views.updateMandate.inputs.performanceFee.label')"
              :min="0"
              :rules="percentageRules"
              density="compact"
              hide-details="auto"
              suffix="%"
              type="number"
            />
          </v-col>
        </v-row>

        <DividerComponent />

        <v-row>
          <v-col class="d-flex align-center" cols="12">
            <v-icon class="mr-2" color="primary" icon="PhSignOut"/>
            <h2 class="subtitle">{{ t("views.updateMandate.subtitle7") }}</h2>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" md="3" sm="6">
            <v-text-field
              v-model.number="form.fees.exitFee"
              :disabled="!canEditFields"
              :label="t('views.updateMandate.inputs.exitFee.label')"
              :min="0"
              :rules="percentageRules"
              density="compact"
              hide-details="auto"
              suffix="%"
              type="number"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" md="3" sm="6">
            <v-text-field
              v-model.number="form.fees.earlyExitPenalty"
              :disabled="!canEditFields"
              :label="t('views.updateMandate.inputs.earlyExitPenalty.label')"
              :min="0"
              :rules="percentageRules"
              density="compact"
              hide-details="auto"
              suffix="%"
              type="number"
            />
          </v-col>
          <v-col cols="12" md="3" sm="6">
            <v-text-field
              v-model.number="form.fees.earlyExitMinDuration"
              :disabled="!form.fees.earlyExitPenalty || (!canEditFields)"
              :label="t('views.updateMandate.inputs.earlyExitMinDuration.label')"
              :min="0"
              :suffix="t('views.updateMandate.inputs.earlyExitMinDuration.suffix', form.fees.earlyExitMinDuration ?? 0)"
              density="compact"
              hide-details="auto"
              type="number"
            />
          </v-col>
        </v-row>
      </v-stepper-window-item>
    </v-stepper-window>
  </v-stepper>
</template>

<style scoped>
.v-stepper {
  margin-top: 24px;
}

.v-stepper-header {
  box-shadow: none;
}
.v-stepper-header .v-divider {
  margin: unset;
}

.v-stepper-item {
  padding: 12px;
}

.v-stepper-window {
  margin: 12px 0 0;
}

.td-btn {
  min-width: 0;
}
</style>
